import { HourglassIcon } from "lucide-react";

import { Row, Text } from "@/components";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  FRESHNESS_STALE_AFTER_HOURS,
  FRESHNESS_WARN_AFTER_HOURS,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { stevieRoute } from "@/lib/stevieRoute";
import { cn } from "@/lib/utils";
import { MinimalCandidacy } from "@/types";

export const TimeInStageDisplay = ({
  candidacy,
  showTooltip = false,
}: {
  candidacy: MinimalCandidacy;
  showTooltip?: boolean;
}) => {
  // A Stevie route restarts the clock: from the nudge while they owe us a
  // call, then from the call once they've taken it.
  const date = stevieRoute.stageEnteredAt(candidacy);
  const hours = dates.calculateHoursAgo(date);
  const label = dates.timeAgoForPipelineCompact(date);
  const isStale = hours >= FRESHNESS_STALE_AFTER_HOURS;
  const isWarn = !isStale && hours >= FRESHNESS_WARN_AFTER_HOURS;

  const textClass = cn(
    "text-[11px] tabular-nums",
    isStale && "font-medium text-red-600",
    isWarn && "font-medium text-orange-600",
    !isStale && !isWarn && "font-normal text-slate-500",
  );
  const iconClass = cn(
    "h-3 w-3 shrink-0",
    isStale && "text-red-500",
    isWarn && "text-orange-500",
    !isStale && !isWarn && "text-slate-400",
  );

  /* Render */
  const renderRow = () => (
    <Row className="items-center gap-1">
      <HourglassIcon className={iconClass} />
      <Text className={textClass}>{label}</Text>
    </Row>
  );

  const render = () => {
    if (!showTooltip) return renderRow();

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>{renderRow()}</TooltipTrigger>
          <TooltipContent>Time in stage</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
